import { MenuItem, Box, Typography } from "@mui/material";
import { Field, FieldArray, Form, Formik } from "formik";
import _ from "lodash";
import { ReactElement } from "react";
import * as Yup from "yup";

import { ActBlueImportIntegration } from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import FormikDatePicker from "~frontend/components/forms/FormikDatePicker";
import FormikErrorBanner from "~frontend/components/forms/FormikErrorBanner";
import theme from "~frontend/theme";

import { FilterForm } from "./FilterForm";

export type ActBlueImportFilterForForm = {
  donationMinimum: string;
  donationMaximum: string;
  deviceType: string;
  donationType: string;
  refcodes: string;
};

export const blankActBlueFilter: ActBlueImportFilterForForm = {
  donationMinimum: "",
  donationMaximum: "",
  deviceType: "",
  donationType: "",
  refcodes: "",
};

export default function ImportForm({
  onSubmit,
  availableActBlueImportIntegrations,
  initialValues,
  readOnly,
  resetContactSourceSettings,
}: {
  onSubmit?: (values: any) => void;
  availableActBlueImportIntegrations: ActBlueImportIntegration[];
  initialValues: any;
  readOnly?: boolean;
  resetContactSourceSettings: (() => void) | null;
}): ReactElement {
  if (!availableActBlueImportIntegrations.length) {
    return <div />; // loading spinner?
  }

  const showIntegrationPicker = availableActBlueImportIntegrations.length > 1;

  const filterSchema = Yup.object({
    donationMinimum: Yup.number()
      .label("Donation minimum")
      .positive()
      .typeError("Donation minimum must be a number"),
    donationMaximum: Yup.number()
      .label("Donation maximum")
      .positive()
      .typeError("Donation maximum must be a number"),
    deviceType: Yup.mixed().oneOf(["MOBILE", "DESKTOP", ""]),
    donationType: Yup.mixed().oneOf(["ONE_TIME", "RECURRING", ""]),
    refcodes: Yup.string().label("Refcodes").nullable(),
  });

  const csvApiQuerySchema = Yup.object({
    integrationId: Yup.string().required("Required"),
    reportStartDate: Yup.date().label("Start date").required("Required"),
    reportEndDate: Yup.date()
      .label("End date")
      .required("Required")
      .when("reportStartDate", (startDate, schema) => {
        return schema.min(startDate, "End date must be after the start date");
      }),
    filters: Yup.array().of(filterSchema),
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={csvApiQuerySchema}
      onSubmit={(values, _formik) => {
        const cleanedFilters = values.filters
          ? values.filters.map((filter: ActBlueImportFilterForForm) => ({
              donationMinimum: Number(filter.donationMinimum) || null,
              donationMaximum: Number(filter.donationMaximum) || null,
              deviceType: filter.deviceType || null,
              donationType: filter.donationType || null,
              refcodes: filter.refcodes
                ? filter.refcodes.split(",").map((rc: string) => rc.trim())
                : [],
            }))
          : [];
        onSubmit?.({ ...values, filters: cleanedFilters });
      }}
    >
      {({ values, dirty }) => {
        return (
          <Form>
            <RouteLeavingGuard when={dirty} />

            <FormikErrorBanner />
            {showIntegrationPicker ? (
              <FormikSelect
                label="Integration"
                name="integrationId"
                withNoneOption={false}
                fullWidth
                sx={{ marginBottom: theme.spacing(2) }}
                disabled={readOnly}
              >
                {availableActBlueImportIntegrations.map(integration => (
                  <MenuItem value={integration.id} key={integration.id}>
                    {integration.name}
                  </MenuItem>
                ))}
              </FormikSelect>
            ) : (
              <Field type="hidden" name="integrationId" />
            )}

            <Typography
              variant="body1"
              component="div"
              sx={{ marginBottom: theme.spacing(2) }}
            >
              Load contacts who donated between the following dates:
            </Typography>
            <FlexBox
              gap={theme.spacing(3)}
              sx={{ marginBottom: theme.spacing(3) }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <FormikDatePicker
                  name="reportStartDate"
                  label="Start Date"
                  format="MMMM d, yyyy"
                  fullWidth={true}
                  disabled={readOnly}
                />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <FormikDatePicker
                  name="reportEndDate"
                  label="End Date"
                  format="MMMM d, yyyy"
                  fullWidth={true}
                  disabled={readOnly}
                />
              </Box>
            </FlexBox>

            <Typography variant="body1Bold">
              Advanced Filters (optional)
            </Typography>
            <Box>
              <Typography variant="body1" sx={{ marginBottom: "1.5em" }}>
                Your list will target contacts who donated between the dates
                above and meet all of the following filter criteria. To add an
                additional set of filter criteria to target, use the “add
                another target” button below.
              </Typography>
              <FieldArray name="filters">
                {({ remove, push }) => (
                  <>
                    {values.filters.map((_filter: any, index: number) => {
                      const isLastFilter = index === values.filters.length - 1;

                      return (
                        <FilterForm
                          key={`filterform${index}`}
                          index={index}
                          onDelete={
                            index > 0 && !readOnly
                              ? (idx: number) => remove(idx)
                              : null
                          }
                          onAdd={
                            isLastFilter && !readOnly
                              ? () => push(blankActBlueFilter)
                              : null
                          }
                          readOnly={readOnly}
                          resetContactSourceSettings={
                            resetContactSourceSettings
                          }
                        />
                      );
                    })}
                  </>
                )}
              </FieldArray>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}
